import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';

const GoatLogsPage: React.FC = () => {
  const { businessType } = useBusinessTypeContext();

  const modules = [
    { 
      id: 'health', 
      name: 'Health Logs', 
      icon: '💊', 
      color: 'bg-red-100 text-red-800',
      href: '/goat/health-logs',
      description: 'Track vaccinations, deworming, and treatments'
    },
    { 
      id: 'weight', 
      name: 'Weight Register', 
      icon: '⚖️', 
      color: 'bg-blue-100 text-blue-800',
      href: '/goat/weight-register',
      description: 'Track and record goat weight measurements'
    },
    { 
      id: 'breeding', 
      name: 'Breeding Logs', 
      icon: '💑', 
      color: 'bg-pink-100 text-pink-800',
      href: '/goat/breeding-logs',
      description: 'Record and track breeding activities'
    },
    { 
      id: 'kidding', 
      name: 'Kidding Logs', 
      icon: '🐐', 
      color: 'bg-green-100 text-green-800',
      href: '/goat/kidding-logs',
      description: 'Record kidding events and births'
    },
    { 
      id: 'breeding-plan', 
      name: 'Breeding Plan', 
      icon: '📅', 
      color: 'bg-purple-100 text-purple-800',
      href: '/goat/breeding-plan',
      description: 'Plan and schedule breeding activities'
    },
    { 
      id: 'sale', 
      name: 'Sale Register', 
      icon: '💰', 
      color: 'bg-emerald-100 text-emerald-800',
      href: '/goat/sale-register',
      description: 'Record goat sales and transactions'
    },
    { 
      id: 'feed', 
      name: 'Feed Logs', 
      icon: '🌾', 
      color: 'bg-amber-100 text-amber-800',
      href: '/goat/feed-logs',
      description: 'View feed consumption history'
    },
    { 
      id: 'medicine', 
      name: 'Medicine Logs', 
      icon: '💊', 
      color: 'bg-indigo-100 text-indigo-800',
      href: '/goat/medicine-logs',
      description: 'View medicine and treatment history'
    },
    { 
      id: 'income', 
      name: 'Income Logs', 
      icon: '💵', 
      color: 'bg-green-100 text-green-800',
      href: '/goat/income-logs',
      description: 'View income and sales records'
    },
    { 
      id: 'expense', 
      name: 'Expense Logs', 
      icon: '💸', 
      color: 'bg-red-100 text-red-800',
      href: '/goat/expense-logs',
      description: 'View expense records and costs'
    },
    { 
      id: 'milk', 
      name: 'Milk Register', 
      icon: '🥛', 
      color: 'bg-gray-100 text-gray-500',
      href: '/goat/milk-register',
      description: 'Record daily milk production and yield',
      disabled: true
    },
  ];

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="text-center">
        <ClipboardDocumentListIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🐐 Goat Logs</h1>
        <p className="text-lg text-gray-600">
          Select a module to view goat farming logs
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((module) => {
          const isDisabled = module.disabled;
          const content = (
            <div className="flex items-center space-x-4">
              <div className="text-3xl">{module.icon}</div>
              <div className="flex-1">
                <h3 className={`font-semibold ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{module.name}</h3>
                <p className={`text-sm mt-1 ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>{module.description}</p>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${module.color}`}>
                  {isDisabled ? 'Disabled' : 'Available'}
                </span>
              </div>
            </div>
          );

          if (isDisabled) {
            return (
              <div
                key={module.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 opacity-60 cursor-not-allowed"
              >
                {content}
              </div>
            );
          }

          return (
            <Link
              key={module.id}
              to={module.href}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200"
            >
              {content}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default GoatLogsPage;
